import {
  ref,
  watch,
  nextTick,
  type Ref,
  readonly,
  type DeepReadonly,
  computed,
  ComputedRef
} from "vue";
import { isNonNullable } from "@zyzgroup/core-common";

// Vue 中 props 未传为 undefined
// 支付宝小程序中 props 未传为 undefined
// 微信小程序中 props 未传为 null

// const [state, setState] = useControllableState<T>({
//   value: () => props.modelValue,
//   defaultValue: props.defaultValue,
//   onChange(nextValue) {
//     emits("update:modelValue", nextValue);
//     emits("change", nextValue);
//   },
//   beforeChange: props.beforeChange
// });
// await setState((v) => ({ ...v })); // immutable

// 受控模式 “controlled” (driven by props), options.onChange 必填
// <Demo v-model="xxx" />
// <Demo v-model:modelValue的替换名称="xxx" />
// <Demo :modelValue的替换名称="xxx" @change="onChange" />

// 非受控模式 “uncontrolled” (driven by state), options.defaultValue 必填
// <Demo :defaultValue="xxx"/>

// 同时支持：受控模式 | 非受控模式
export function useControllableState<T = any>(options: {
  value: () => unknown; // watch source
  defaultValue?: T;
  onChange?: (value: T) => void;
  // 异步/同步拦截器：权限 / 日志 / 埋点 / 风控 / 二次确认
  beforeChange?: (nextValue: T) => boolean | Promise<boolean>;
}): readonly [
  // state: DeepReadonly<Ref<T>>,
  state: ComputedRef<T>,
  setState: (newValue: T | ((prev: T) => T)) => Promise<void>
] {
  const initialValue = options.value();

  // 是否是：受控模式，完全受控于父组件
  const controlled = isNonNullable(initialValue);

  if (!controlled && !("defaultValue" in options)) {
    throw new Error("defaultValue is required in uncontrolled mode");
  }
  if (controlled && !("onChange" in options)) {
    throw new Error("onChange is required in controlled mode");
  }

  // 内部状态
  const inner = ref<T>((controlled ? initialValue : options.defaultValue) as T);

  // 对外状态
  const state = computed<T>(() =>
    controlled ? (options.value() as T) : inner.value
  );

  // 同步外部状态
  let warned = false;
  watch(
    options.value,
    (v) => {
      if (!warned && isNonNullable(v) !== controlled) {
        warned = true;
        // side effect
        console.warn(
          "[useControllableState] can't switch between controlled and uncontrolled mode."
        );
        return;
      }
      // if (controlled) {
      //   return;
      // }
      // // 避免无限循环：只有在新值与旧值不同时才会重新触发
      // // 即：props.modelValue !== update:modelValue
      // if (!Object.is(inner.value, v)) {
      //   inner.value = (v ?? options.defaultValue) as T;
      // }
    },
    {
      // immediate: true
    }
  );

  let version = 0;
  async function setState(newValue: T | ((prev: T) => T)) {
    const currentVersion = ++version;

    const nextValue: T =
      typeof newValue === "function"
        ? (newValue as (prev: T) => T)(state.value)
        : newValue;

    if (options.beforeChange) {
      const pass = await options.beforeChange(nextValue);
      if (!pass) {
        return;
      }
    }

    if (currentVersion !== version) {
      return;
    }

    if (!controlled) {
      inner.value = nextValue;
    }

    options.onChange?.(nextValue);

    // 非受控模式下通常已更新 DOM
    // 受控模式下不能保证父组件已完成更新
    await nextTick();
  }

  // return [readonly(state), setState] as const;
  return [state, setState] as const;
}
